import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import useToast from "~/components/ui/toast";
import { ROUTE_NOVA_VIEW } from "~/router/constants";
import { deleteAgent, mutateAgentList } from "~/services/agents";

interface UseDeleteAgentReturn {
  handleDelete: (agentId: string, displayName: string) => Promise<void>;
  isDeleting: boolean;
}

/**
 * Deletes an agent and leaves its now-dead page for the agents list.
 *
 * Gate call sites on `useCanDeleteAgent`; this hook does not check.
 */
export function useDeleteAgent(): UseDeleteAgentReturn {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(
    async (agentId: string, displayName: string) => {
      setIsDeleting(true);
      try {
        await deleteAgent(agentId);
        await mutateAgentList();
        showSuccess(`"${displayName}" deleted`);
        navigate(`${ROUTE_NOVA_VIEW}/agents`);
      } catch {
        showError("Failed to delete agent");
      } finally {
        setIsDeleting(false);
      }
    },
    [navigate, showSuccess, showError]
  );

  return { handleDelete, isDeleting };
}
